/**
 * Satori Moment — Surprise Awakening Overlay
 *
 * Appears once per session when a breakthrough is detected
 * (pattern confirmed at high emotional intensity).
 *
 * "A moment of awakening just arrived."
 *
 * Full-screen sacred pause. Optional body-noticing input.
 * Auto-dismissed only by the user — never auto-skipped.
 */
import { useState, useEffect } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { useTranslation } from 'react-i18next'

interface SatoriMomentProps {
  onComplete: () => void
}

export default function SatoriMoment({ onComplete }: SatoriMomentProps) {
  useScreenId('see.satori')
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const [textVisible, setTextVisible] = useState(false)
  const [inputVisible, setInputVisible] = useState(false)
  const [noticing, setNoticing] = useState('')

  // Staggered entrance
  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 60)
    const t2 = setTimeout(() => setTextVisible(true), 700)
    const t3 = setTimeout(() => setInputVisible(true), 1600)
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3) }
  }, [])

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col items-center justify-center px-8"
      style={{
        background: 'linear-gradient(160deg, #FDFAF5 0%, #F0E8FF 100%)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.6s ease',
      }}
    >
      {/* Pulsing amber light */}
      <div style={{ position: 'relative', width: 120, height: 120, marginBottom: '2.5rem' }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,96,0.15) 0%, transparent 70%)',
          animation: 'satoriPulse 3s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', inset: '22px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,96,0.3) 0%, transparent 70%)',
          animation: 'satoriPulse 3s ease-in-out infinite 0.8s',
        }} />
        <div style={{
          position: 'absolute', inset: '46px', borderRadius: '50%',
          background: '#D4AF60',
          opacity: 0.7,
          animation: 'satoriPulse 3s ease-in-out infinite 1.6s',
        }} />
        <style>{`
          @keyframes satoriPulse {
            0%, 100% { transform: scale(0.9); opacity: 0.6; }
            50%       { transform: scale(1.1); opacity: 1;   }
          }
        `}</style>
      </div>

      {/* Text */}
      <div
        className="text-center max-w-xs"
        style={{
          opacity: textVisible ? 1 : 0,
          transform: textVisible ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        <p className="text-xs font-medium uppercase tracking-widest mb-5"
          style={{ color: '#D4AF60' }}>
          {t('satori.awakening')}
        </p>

        <p className="font-display text-2xl leading-snug mb-3"
          style={{ color: '#1C1128', fontStyle: 'italic', fontWeight: 300 }}>
          {t('satori.momentArrived')}
        </p>

        <p className="text-sm leading-relaxed mb-10"
          style={{ color: '#6B5F7A' }}>
          {t('satori.pauseHere')}
        </p>
      </div>

      {/* Input */}
      <div
        className="w-full max-w-sm"
        style={{
          opacity: inputVisible ? 1 : 0,
          transform: inputVisible ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        <p className="text-sm text-center mb-4"
          style={{ color: '#6B5F7A' }}>
          {t('satori.whatIsShifting')}
        </p>

        <textarea
          value={noticing}
          onChange={e => setNoticing(e.target.value)}
          placeholder={t('satori.wordOrTwo')}
          rows={3}
          maxLength={1000}
          className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
          style={{
            borderColor: 'rgba(212,175,96,0.35)',
            background: 'white',
            color: '#1C1128',
          }}
          onFocus={e => { e.target.style.borderColor = '#D4AF60'; e.target.style.boxShadow = '0 0 0 3px rgba(212,175,96,0.12)' }}
          onBlur={e => { e.target.style.borderColor = 'rgba(212,175,96,0.35)'; e.target.style.boxShadow = 'none' }}
        />

        <button
          onClick={onComplete}
          className="w-full py-4 rounded-xl font-medium tracking-wide mt-4"
          style={{ background: 'rgba(212,175,96,0.12)', color: '#8A6800', border: '1px solid rgba(212,175,96,0.4)', cursor: 'pointer' }}
        >
          {t('satori.iFeelIt')}
        </button>
        <button
          onClick={onComplete}
          className="w-full py-3 rounded-xl text-sm mt-2"
          style={{ background: 'transparent', border: 'none', color: '#9B8FAA', cursor: 'pointer' }}
        >
          {t('session.continue')}
        </button>
      </div>
    </div>
  )
}
